import type { PlanetId } from '../../content/planets';
import {
  JUNGLE_GROUND,
  paintCanopy,
  paintForeground,
  paintMountains,
  paintSky,
  type GroundLook,
} from './art';
import {
  EARTH_COLORS,
  EARTH_GROUND,
  paintEarthCity,
  paintEarthForeground,
  paintEarthSky,
  paintEarthStreet,
} from './earthArt';
import {
  MARS_COLORS,
  MARS_GROUND,
  paintMarsForeground,
  paintMarsMesas,
  paintMarsPillars,
  paintMarsSky,
} from './marsArt';
import {
  MOON_COLORS,
  MOON_GROUND,
  paintMoonForeground,
  paintMoonHighlands,
  paintMoonRims,
  paintMoonSky,
} from './moonArt';
import { JUNGLE } from './palette';

type Paint = (ctx: CanvasRenderingContext2D, width: number, height: number) => void;

/**
 * How one world looks: the scenery layers back to front (sky, far, mid, the foreground in front
 * of the lanes), the ground, and the haze that farther lanes fade into (GDD: mood per planet).
 */
export interface Biome {
  readonly sky: Paint;
  readonly far: Paint;
  readonly mid: Paint;
  readonly foreground: Paint;
  readonly ground: GroundLook;
  readonly haze: string;
  /** The crawler (wildlife) recoloured for this world: its palette keys and new colours. */
  readonly crawler?: Readonly<Record<string, string>>;
}

/** Earth (the tutorial), the planets of a run, and the jungle (its test room). */
export type BiomeId = 'earth' | 'jungle' | PlanetId;

export const BIOMES: Readonly<Record<BiomeId, Biome>> = {
  earth: {
    sky: paintEarthSky,
    far: paintEarthCity,
    mid: paintEarthStreet,
    foreground: paintEarthForeground,
    ground: EARTH_GROUND,
    haze: EARTH_COLORS.haze,
  },
  moon: {
    sky: paintMoonSky,
    far: paintMoonHighlands,
    mid: paintMoonRims,
    foreground: paintMoonForeground,
    ground: MOON_GROUND,
    haze: MOON_COLORS.haze,
    // Moon crawlers: pale shells, cold cyan eyes.
    crawler: { p: '#8a90a8', P: '#c8d0e8', q: '#4a506a', g: '#7df9ff', G: '#e8ffff' },
  },
  mars: {
    sky: paintMarsSky,
    far: paintMarsMesas,
    mid: paintMarsPillars,
    foreground: paintMarsForeground,
    ground: MARS_GROUND,
    haze: MARS_COLORS.haze,
    // Rust beetles with yellow eyes.
    crawler: { p: '#a8442a', P: '#e07848', q: '#5a2214', g: '#ffd23f', G: '#fff6c8' },
  },
  jungle: {
    sky: paintSky,
    far: paintMountains,
    mid: paintCanopy,
    foreground: paintForeground,
    ground: JUNGLE_GROUND,
    haze: JUNGLE.haze,
  },
};
